import { useState } from 'react';
import { Loader2, RefreshCw, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

const MAX_DISPLAY_ROWS = 50;

export function CancelledMgmtFeeBackfillPanel() {
  const [running, setRunning] = useState(false);
  const [confirmApply, setConfirmApply] = useState(false);
  const [results, setResults] = useState(null);

  const run = async (dryRun) => {
    setRunning(true);
    setResults(null);
    setConfirmApply(false);

    try {
      const { data } = await diagnosticsAPI.backfillCancelledManagementFee({
        dryRun,
      });
      setResults(data);

      if (!dryRun && data.updated_count > 0) {
        toast.success(
          `Management fee backfill applied: ${data.updated_count} bookings updated, ` +
          `total delta: ${data.total_delta}`
        );
      }
    } catch (err) {
      setResults({ error: getErrorMessage(err) });
      toast.error(getErrorMessage(err));
    } finally {
      setRunning(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <RefreshCw className="h-5 w-5 text-blue-500" />
          Cancelled Booking Management Fee Backfill
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Sets management_fee = net_revenue on cancelled managed bookings (100% to Fan).
          Sets management_fee = 0 on cancelled owned bookings. Preview shows mismatches;
          Apply corrects them.
        </p>

        <div className="flex gap-2">
          <Button onClick={() => run(true)} disabled={running}>
            {running ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Preview
          </Button>

          {!confirmApply ? (
            <Button
              variant="destructive"
              onClick={() => setConfirmApply(true)}
              disabled={running}
            >
              Apply
            </Button>
          ) : (
            <Button
              variant="destructive"
              onClick={() => run(false)}
              disabled={running}
            >
              {running ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <AlertTriangle className="h-4 w-4 mr-2" />
              )}
              Confirm Apply
            </Button>
          )}
        </div>

        {results && (
          <div className="space-y-4">
            {results.error && (
              <div className="text-sm text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-lg p-3">
                Error: {results.error}
              </div>
            )}

            {!results.error && (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="rounded-lg border p-3">
                    <p className="text-xs text-muted-foreground">Mismatched</p>
                    <p className="text-xl font-bold tabular-nums">{results.affected_count}</p>
                  </div>
                  <div className="rounded-lg border p-3">
                    <p className="text-xs text-muted-foreground">
                      {results.dry_run ? 'Would update' : 'Updated'}
                    </p>
                    <p className="text-xl font-bold tabular-nums">
                      {results.dry_run ? results.affected_count : results.updated_count}
                    </p>
                  </div>
                  <div className="rounded-lg border p-3">
                    <p className="text-xs text-muted-foreground">Total delta</p>
                    <p className="text-xl font-bold tabular-nums">{results.total_delta}</p>
                  </div>
                  <div className="rounded-lg border p-3">
                    <p className="text-xs text-muted-foreground">Mode</p>
                    <Badge variant={results.dry_run ? 'secondary' : 'default'}>
                      {results.dry_run ? 'Preview' : 'Applied'}
                    </Badge>
                  </div>
                </div>

                {results.bookings?.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-sm font-medium">
                      {results.bookings.length > MAX_DISPLAY_ROWS
                        ? `Showing ${MAX_DISPLAY_ROWS} of ${results.bookings.length} bookings`
                        : `${results.bookings.length} booking${results.bookings.length !== 1 ? 's' : ''}`}
                    </p>
                    <div className="overflow-x-auto border rounded-lg">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-muted/50">
                            <th className="text-left px-3 py-2 font-medium">ID</th>
                            <th className="text-left px-3 py-2 font-medium">Guest</th>
                            <th className="text-left px-3 py-2 font-medium">Property</th>
                            <th className="text-left px-3 py-2 font-medium">Type</th>
                            <th className="text-right px-3 py-2 font-medium">Net Revenue</th>
                            <th className="text-right px-3 py-2 font-medium">Old Mgmt Fee</th>
                            <th className="text-right px-3 py-2 font-medium">New Mgmt Fee</th>
                            <th className="text-right px-3 py-2 font-medium">Delta</th>
                          </tr>
                        </thead>
                        <tbody>
                          {results.bookings.slice(0, MAX_DISPLAY_ROWS).map((r, i) => (
                            <tr key={r.id || i} className="border-b last:border-0">
                              <td className="px-3 py-2 font-mono text-xs">
                                {r.id}
                                <Badge variant="outline" className="ml-1 text-[10px]">{r.platform}</Badge>
                              </td>
                              <td className="px-3 py-2 text-xs">{r.guest_name}</td>
                              <td className="px-3 py-2 text-xs">{r.property_name}</td>
                              <td className="px-3 py-2">
                                <Badge variant={r.management_type === 'managed' ? 'default' : 'outline'}
                                       className="text-[10px]">
                                  {r.management_type}
                                </Badge>
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums">
                                {Number(r.net_revenue).toFixed(2)}
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums text-amber-600 dark:text-amber-400">
                                {Number(r.old_management_fee).toFixed(2)}
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums text-green-600 dark:text-green-400">
                                {Number(r.new_management_fee).toFixed(2)}
                              </td>
                              <td className="px-3 py-2 text-right tabular-nums">
                                {Number(r.delta).toFixed(2)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {results.affected_count === 0 && (
                  <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                    <CheckCircle2 className="h-4 w-4" />
                    All cancelled bookings have correct management_fee values.
                  </div>
                )}

                {results.dry_run === false && results.updated_count > 0 && (
                  <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                    <CheckCircle2 className="h-4 w-4" />
                    Backfill complete. {results.updated_count} booking{results.updated_count !== 1 ? 's' : ''} corrected.
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
